/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : Checkbox / Toggle
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical token-styled boolean controls. Checkbox for forms
 *                  and filter lists; Toggle (switch) for on/off settings like
 *                  auto-refresh or enable/disable. Replaces ad-hoc
 *                  `<input type="checkbox">` and hand-rolled switches.
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import React, { forwardRef, InputHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: React.ReactNode;
}

/** Canonical checkbox. Uses CSS `accent-color` token so the check matches the theme. */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, className, id, disabled, ...props }, ref) => {
    const cid = id || `cb-${Math.random().toString(36).slice(2, 9)}`;
    return (
      <label
        htmlFor={cid}
        className={clsx('inline-flex select-none items-center gap-2', disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
      >
        <input
          ref={ref}
          id={cid}
          type="checkbox"
          disabled={disabled}
          className={clsx(
            'h-4 w-4 rounded border-[var(--color-border)] accent-[var(--color-accent)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]',
            className,
          )}
          {...props}
        />
        {label != null && <span className="text-sm text-[var(--color-text-primary)]">{label}</span>}
      </label>
    );
  },
);
Checkbox.displayName = 'Checkbox';

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

/** Canonical on/off switch (settings, auto-refresh, enable/disable). */
export const Toggle: React.FC<ToggleProps> = ({ checked, onChange, label, disabled, className, ...rest }) => (
  <label
    className={clsx('inline-flex select-none items-center gap-2', disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer', className)}
  >
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={clsx(
        'relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-1',
        checked ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-border)]',
      )}
      {...rest}
    >
      <span
        className={clsx(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-4' : 'translate-x-0.5',
        )}
      />
    </button>
    {label != null && <span className="text-sm text-[var(--color-text-primary)]">{label}</span>}
  </label>
);

export default Checkbox;
